import * as JSZip from "jszip";
import { defer, forkJoin, Observable, of } from "rxjs";
import { map, mergeMap } from "rxjs/operators";

import { HttpErrorResponse, HttpEvent, HttpResponse, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { FormatDate } from "../../_objects/format-date";
import {
  SubjectLocation,
  SubjectModel,
  SubjectModelClone,
  SubjectModelDetail,
  SubjectModelListItem,
  SubjectModelNew,
  SubjectModelUpdate
} from "../../_objects/subject-model";
import { HttpErrorMessage, HttpStatusText } from "../../_offline/electron.constants";
import { SubjectModelLocationsSchema } from "../../_offline/models/subjectModel-locations.schema";
import { SubjectModelSchema } from "../../_offline/models/subjectModel.schema";
import { UtilsService } from "../_utils.service";
import { SubjectModelDbService } from "../database/subject-model-db.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { SubjectModelContractService } from "../interfaces/subject-model-contract.service";
import { LoginUserOfflineService } from "./login-user-offline.service";

@Injectable({
  providedIn: "root"
})
export class SubjectModelOfflineService implements SubjectModelContractService {
  constructor(
    private fileSystemAccessService: FileSystemAccessService,
    private loginUserOfflineService: LoginUserOfflineService,
    private subjectModelDbService: SubjectModelDbService,
    private utilsService: UtilsService
  ) {}

  getSubjectModels(enabledOnly?: boolean, aliveOnly?: boolean): Observable<SubjectModelListItem[]> {
    return this.subjectModelDbService.getAll(enabledOnly, aliveOnly).pipe(
      map((schemas) => {
        return schemas.map((schema) => {
          const item = new SubjectModelListItem();
          item.id = schema.id;
          item.name = schema.name;
          item.isValid = schema.isValid;
          item.lastUpdateDate = schema.lastUpdateDate;

          const newestLocations = schema.subjectModelLocations.find((_) => _.isNewestVersion);
          item.version = newestLocations?.version;
          return item;
        }).sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  getSubjectModel(id: string, version?: number, enabledOnly?: boolean, aliveOnly?: boolean): Observable<SubjectModel> {
    return this.subjectModelDbService.getOne(id, enabledOnly, aliveOnly).pipe(
      map((schema) => {
        const result = new SubjectModel();
        result.setSubjectModelSchema(schema, version);
        return result;
      })
    );
  }

  addSubjectModel(subjectModel: SubjectModelNew): Observable<SubjectModel> {
    return of();
  }
  updateSubjectModel(updateModel: SubjectModelUpdate): Observable<any> {
    return of();
  }
  exportSubjectModel(id: string): Observable<Blob> {
    return of();
  }

  importSubjectModel(file: File): Observable<HttpEvent<{}>> {
    let subjectModel: SubjectModelDetail;
    let jsonString: string = "";
    let imageFileName: string = "";
    let imageBlob: Blob;

    return defer(() => JSZip.loadAsync(file)).pipe(
      mergeMap((x) => {
        const compressedFiles: Array<Observable<any>> = new Array<Observable<any>>();
        x.forEach((relativePath: string, file: JSZip.JSZipObject) => {
          if (file.name.endsWith(".json")) {
            compressedFiles.push(
              defer(async () => {
                return await file.async("string").then((x) => {
                  // JSON の取得。
                  jsonString = x;
                  return x;
                });
              })
            );
          } else {
            imageFileName = file.name;
            compressedFiles.push(
              defer(async () => {
                return await file.async("blob").then((x) => {
                  // 画像データの取得。
                  imageBlob = x;
                  return x;
                });
              })
            );
          }
        });

        return forkJoin(compressedFiles);
      }),
      mergeMap(() => {
        let parsedObject: any;
        try {
          parsedObject = JSON.parse(jsonString);
        } catch (error) {
          console.log(error);

          // JSON 文字列のオブジェクト化に失敗した場合。
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.BadRequest.DataImport}${error}`,
            status: HttpStatusCode.BadRequest,
            statusText: HttpStatusText.BadRequest
          });
        }

        subjectModel = new SubjectModelDetail();
        const result: string = subjectModel.setValueFromJsonObject(parsedObject);

        if (result != "") {
          console.log(result);

          // 項目チェックエラーがある場合。
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.BadRequest.DataImport}${result}`,
            status: HttpStatusCode.BadRequest,
            statusText: HttpStatusText.BadRequest
          });
        }

        return this.subjectModelDbService.isSameSubjectModelNameExists(this.loginUserOfflineService.tenantId, subjectModel.name);
      }),
      mergeMap((x) => {
        // 同一 Subject model 名存在チェック。 <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Db/Sprocs/AnySubjectModelHasSameModelName.sql&version=GBdev>
        if (x) {
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.Conflict.SubjectModelAlreadyExists}${subjectModel.name}`,
            status: HttpStatusCode.Conflict,
            statusText: HttpStatusText.Conflict
          });
        }

        // SubjectLocation の identifier 重複チェック。
        if (!this.isLocationNameUnique(subjectModel.locations)) {
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.Conflict.SubjectModelLocationIdentifierDuplicated}`,
            status: HttpStatusCode.Conflict,
            statusText: HttpStatusText.Conflict
          });
        }

        const subjectModelSchema: SubjectModelSchema = new SubjectModelSchema(subjectModel, this.loginUserOfflineService.tenantId);
        const subjectModelLocationsSchema: SubjectModelLocationsSchema = new SubjectModelLocationsSchema(
          subjectModel.id,
          1,
          subjectModel.locations,
          true
        );
        return this.subjectModelDbService.putImportedSubjectModel(subjectModelSchema, subjectModelLocationsSchema);
      }),
      mergeMap((x) => {
        if (!x) {
          // DB のトランザクションに失敗した場合はエラーを返す。
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.InternalServerError.SQLError}`,
            status: HttpStatusCode.InternalServerError,
            statusText: HttpStatusText.InternalServerError
          });
        }

        if (imageFileName == "") {
          // 画像ファイルが無い場合は画像保存処理をせずに終了する。
          return of(new HttpResponse());
        }

        // 取り込む画像ファイル名は「struct.<元の拡張子>」とする。
        const structFileName: string = ["struct", this.utilsService.getExtension(imageFileName)].join(".");
        const structImageFilePath: string = `subjectModels/${subjectModel.id}/images/${structFileName}`;

        return forkJoin([
          this.subjectModelDbService.getOne(subjectModel.id),
          defer(() => this.utilsService.fileToBase64(new File([imageBlob], structFileName, { type: this.utilsService.getMimeType(structFileName) }))),
          defer(() => this.fileSystemAccessService.getFileFullPath(structImageFilePath.replace(/\//g, "\\")))
        ]).pipe(
          mergeMap((x) => {
            // 画像ファイルパスの DB 書き込み、画像ファイルの保存を行う。
            const subjectModelSchema: SubjectModelSchema = x[0];
            const base64StructImage: string = x[1];
            const structImageFileFullPath: string = x[2];

            subjectModelSchema.structImageFilePath = structImageFilePath;
            subjectModelSchema.lastUpdateDate = new FormatDate().toString();

            return this.subjectModelDbService.putImportedSubjectModelImage(
              subjectModelSchema,
              base64StructImage,
              structImageFileFullPath,
              this.utilsService.getMimeType(structFileName)
            );
          }),
          mergeMap((x) => {
            if (!x) {
              // DB のトランザクションに失敗した場合はエラーを返す。
              throw new HttpErrorResponse({
                error: `${HttpErrorMessage.InternalServerError.SQLError}`,
                status: HttpStatusCode.InternalServerError,
                statusText: HttpStatusText.InternalServerError
              });
            }

            return of(new HttpResponse());
          })
        );
      })
    );
  }

  updateSubjectModelImage(modelId: string, file: File): Observable<HttpEvent<{}>> {
    return of();
  }

  deleteSubjectModel(modelId: string): Observable<HttpEvent<{}>> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobsPath) => this.subjectModelDbService.delete(modelId, blobsPath)),
      mergeMap((x) => {
        if (!x) {
          // DB のトランザクションに失敗した場合はエラーを返す。
          throw new HttpErrorResponse({
            error: `${HttpErrorMessage.InternalServerError.SQLError}`,
            status: HttpStatusCode.InternalServerError,
            statusText: HttpStatusText.InternalServerError
          });
        }

        return of(new HttpResponse());
      })
    );
  }

  cloneSubjectModel(cloneModel: SubjectModelClone): Observable<any> {
    return of();
  }

  /**
   * 子階層を含む SubjectLocation.identifier が一意かどうかを確認する。(大文字小文字区別無し)
   * Backend の SubjectModelLocationHelper.AreUniqueIdentifiers(List<SubjectModelLocation> locations, List<string> allIdentifiersOfLocations) を参考に実装。
   * <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Api/Lib/Helpers/Subject/SubjectModelLocationHelper.cs&version=GBdev&_a=contents>
   *
   * @param locations SubjectLocation の配列。
   * @returns identifier が全て一意であれば true を返す。
   */
  private isLocationNameUnique(locations: Array<SubjectLocation>): boolean {
    /**
     * 子階層を含む SubjectLocation.identifier の一覧を取得する。
     *
     * @param locations
     * @returns 子階層を含む identifier の一覧を返す。
     */
    function getLocationIdentifiers(locations: Array<SubjectLocation>): Array<string> {
      const identifiers: Array<string> = new Array<string>();

      for (const location of locations) {
        identifiers.push(location.identifier.toLowerCase());

        if (!!location.children && location.children.length > 0) {
          identifiers.push(...getLocationIdentifiers(location.children));
        }
      }

      return identifiers;
    }

    const identifiers: Array<string> = getLocationIdentifiers(locations);

    for (const identifier of identifiers) {
      if (identifiers.filter((x) => x == identifier).length > 1) {
        return false;
      }
    }

    return true;
  }
}
